import React from 'react';
import GET_RECENT_UPDATES_NOTIFICATIONS_QUERY from '../../queries/recentUpdatesNotificationsQuery';
import ApolloError from '@shared/components/ApolloError';
import { useCacheNetworkQuery } from '@apollo-internal/hooks/useCacheNetworkQuery';
import NotificationSection from '../shared/NotificationSection';
import { RECENT_UPDATES_NOTIFICATION_TYPES } from './constants/recentUpdatesNotificationTypes';
import AppraisalResultNotification from './recentUpdatesNotificationTypeComponents/AppraisalResultNotification';
import PerformanceReportNotification from './recentUpdatesNotificationTypeComponents/PerformanceReportNotification';
import ReappraisalRationaleNotification from './recentUpdatesNotificationTypeComponents/ReappraisalRationaleNotification';
import CheckpointNotification from './recentUpdatesNotificationTypeComponents/CheckpointNotification';
import ChangeDateRequestEditEventReviewNotification from './recentUpdatesNotificationTypeComponents/ChangeDateRequestEditEventReviewNotification';
import ChangeDateRequestReviewPageNotification from './recentUpdatesNotificationTypeComponents/ChangeDateRequestReviewPageNotification';
import PrivilegeNames from '../../../../constants/privilegeNames';
import ObservationReportReviewPageNotification from './recentUpdatesNotificationTypeComponents/ObservationReportReviewPageNotification';

const NOTIFICATION_TYPE_COMPONENTS_MAP = new Map([
  [RECENT_UPDATES_NOTIFICATION_TYPES.APPRAISAL_RESULTS, AppraisalResultNotification],
  [RECENT_UPDATES_NOTIFICATION_TYPES.PERFORMANCE_REPORTS, PerformanceReportNotification],
  [RECENT_UPDATES_NOTIFICATION_TYPES.REAPPRAISAL_RATIONALES, ReappraisalRationaleNotification],
  [RECENT_UPDATES_NOTIFICATION_TYPES.CHECKPOINTS, CheckpointNotification],
  [
    RECENT_UPDATES_NOTIFICATION_TYPES.CHANGE_DATE_REQUEST_EDIT_EVENT_REVIEW,
    ChangeDateRequestEditEventReviewNotification
  ],
  [
    RECENT_UPDATES_NOTIFICATION_TYPES.CHANGE_DATE_REQUEST_REVIEW_PAGE,
    ChangeDateRequestReviewPageNotification
  ],
  [
    RECENT_UPDATES_NOTIFICATION_TYPES.OBSERVATION_REPORT_REVIEW_PAGE,
    ObservationReportReviewPageNotification
  ]
]);

const RecentUpdatesNotifications = () => {
  const { data, error, loading } = useCacheNetworkQuery(GET_RECENT_UPDATES_NOTIFICATIONS_QUERY);

  const notifications = data?.recentUpdatesNotifications ?? [];

  if (error) {
    return <ApolloError />;
  }

  return (
    <NotificationSection
      notifications={notifications}
      notificationConfig={notificationConfig}
      loading={loading}
    />
  );
};

const filterOptions = [
  {
    value: RECENT_UPDATES_NOTIFICATION_TYPES.APPRAISAL_RESULTS,
    label: 'Appraisal Results',
    displayName: 'Appraisal Results'
  },
  {
    value: RECENT_UPDATES_NOTIFICATION_TYPES.CHECKPOINTS,
    label: 'Check Points',
    displayName: 'Check Points',
    shouldDisplay: privileges => privileges.includes(PrivilegeNames.CheckPoints)
  },
  {
    value: RECENT_UPDATES_NOTIFICATION_TYPES.PERFORMANCE_REPORTS,
    label: 'Performance Reports',
    displayName: 'Performance Reports'
  },
  {
    value: RECENT_UPDATES_NOTIFICATION_TYPES.REAPPRAISAL_RATIONALES,
    label: 'Reappraisal Rationales',
    displayName: 'Reappraisal Rationales'
  },
  {
    value: RECENT_UPDATES_NOTIFICATION_TYPES.CHANGE_DATE_REQUEST_EDIT_EVENT_REVIEW,
    label: 'Date Change',
    displayName: 'Date Change',
    shouldDisplay: privileges =>
      privileges.includes(PrivilegeNames.Calendar_EditEvent_ChangeDateRequest_Review)
  },
  {
    value: RECENT_UPDATES_NOTIFICATION_TYPES.CHANGE_DATE_REQUEST_REVIEW_PAGE,
    label: 'Date Change',
    displayName: 'Date Change',
    shouldDisplay: privileges =>
      privileges.includes(PrivilegeNames.Calendar_ChangeDateRequestReview)
  },
  {
    value: RECENT_UPDATES_NOTIFICATION_TYPES.OBSERVATION_REPORT_REVIEW_PAGE,
    label: 'Observation Report',
    displayName: 'Observation Report',
    shouldDisplay: privileges => privileges.includes(PrivilegeNames.ObservationReport_Review)
  }
];

const notificationConfig = {
  title: 'Recent Updates',
  emptyListText: 'No Updates',
  filterOptions: filterOptions,
  notificationComponentMap: NOTIFICATION_TYPE_COMPONENTS_MAP
};

export default RecentUpdatesNotifications;
